import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CareStageCardData } from './understandCareStageData';
import { apiClient } from '@/core/api/client';

type CardsApiResponse = {
  overviewNarrative: string | null;
  cards: CareStageCardData[];
};

type UnderstandCareStageProps = { careStageId?: string | null };

const getStoredCareStageId = (): string | null =>
  sessionStorage.getItem('bquest_care_stage_id');

export function UnderstandCareStage({ careStageId }: UnderstandCareStageProps) {
  const resolvedCareStageId = careStageId ?? getStoredCareStageId();

  const { data } = useQuery<CardsApiResponse>({
    queryKey: ['care-stage-consideration-cards', resolvedCareStageId],
    enabled: !!resolvedCareStageId,
    queryFn: async () => {
      const { data } = await apiClient.get<CardsApiResponse>(
        '/resources/care-stage-consideration-cards',
        { params: { care_stage_id: resolvedCareStageId } }
      );
      return data;
    },
  });

  const careStageCards = data?.cards ?? [];
  const overviewNarrative = data?.overviewNarrative ?? null;

  const [passedCardIds, setPassedCardIds] = useState<Set<string>>(new Set());
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  useEffect(() => {
    if (!careStageCards.length) return;
    const OFFSET = 220;

    const handleScroll = () => {
      const y = window.scrollY + OFFSET;
      const next = new Set<string>();

      // Track which cards have been scrolled past (for connector line fill)
      for (const card of careStageCards) {
        const el = document.getElementById(`care-card-${card.id}`);
        if (el && el.offsetTop <= y) next.add(card.id);
      }
      setPassedCardIds(next);

      // Track the active (currently focused) card — last one whose top is above viewport midpoint
      const midY = window.scrollY + window.innerHeight / 2;
      let currentId: string | null = careStageCards[0]?.id ?? null;
      for (const card of careStageCards) {
        const el = document.getElementById(`care-card-${card.id}`);
        if (el && el.offsetTop <= midY) currentId = card.id;
      }
      setActiveCardId(currentId);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [careStageCards]);

  if (!careStageCards.length) return null;

  return (
    <section className="py-4">
      {/* Section heading */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900">Understand Your Care Stage</h2>
        <p className="mt-2 text-sm text-gray-500">Explore key areas of your care planning journey.</p>
        {overviewNarrative && (
          <p className="mt-4 text-base leading-7 text-gray-700">{overviewNarrative}</p>
        )}
      </div>

      {/* Cards — full width, single column, timeline connector */}
      <div className="flex flex-col">
        {careStageCards.map((card, index) => {
          const isLast = index === careStageCards.length - 1;
          const isActive = card.id === activeCardId;
          const isPassed = passedCardIds.has(card.id);

          return (
            <div
              key={card.id}
              id={`care-card-${card.id}`}
              className={`flex gap-4 ${isLast ? '' : 'pb-5'}`}
            >
              {/* Left: number circle + vertical connecting line */}
              <div className="flex flex-shrink-0 flex-col items-center">
                {/* Milestone dot — ref: .timeline-milestone-dot / .timeline-journey-step.active .timeline-milestone-dot */}
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-bold transition-all duration-300 ${
                    isActive
                      ? 'scale-[1.15] border-[#026857] bg-[#026857] text-white shadow-[0_0_10px_rgba(2,104,87,0.35)]'
                      : 'border-gray-200 bg-white text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
                {!isLast && (
                  <div
                    className={`mt-1 w-0.5 flex-1 rounded-full transition-colors duration-500 ${
                      isPassed ? 'bg-[#026857]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>

              {/* Card body — ref: .timeline-journey-card / .timeline-journey-step.active .timeline-journey-card */}
              <div
                className={`flex-1 rounded-2xl border p-5 transition-all duration-400 ${
                  isActive
                    ? 'scale-[1.005] border-[#026857] bg-[#fbfcfa] shadow-[0_8px_24px_rgba(2,104,87,0.08)]'
                    : 'scale-[0.985] border-gray-200 bg-white opacity-65 grayscale-[10%]'
                }`}
              >
                <h3
                  className={`mb-4 font-semibold transition-colors duration-300 ${
                    isActive ? 'text-[#026857]' : 'text-gray-900'
                  }`}
                >
                  {index + 1}. {card.title}
                </h3>

                {card.description && (
                  <p className="mb-4 text-sm leading-6 text-gray-600">{card.description}</p>
                )}

                <div className="grid gap-5 md:grid-cols-2">
                  {/* Left: body + why this matters */}
                  <div className="space-y-3">
                    <p className="text-sm leading-6 text-gray-600">{card.popup.body}</p>

                    {card.popup.whyItMatters && (
                      <div className="rounded-lg border border-[#026857]/15 bg-[#026857]/5 p-3">
                        <p className="mb-1 text-xs font-semibold text-gray-800">Why this matters:</p>
                        <p className="text-xs leading-5 text-gray-600">{card.popup.whyItMatters}</p>
                      </div>
                    )}
                  </div>

                  {/* Right: considerations + professionals */}
                  <div className="space-y-4">
                    {card.popup.considerations.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-semibold text-gray-800">
                          As you think about this, consider:
                        </p>
                        <ul className="space-y-1.5">
                          {card.popup.considerations.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-xs leading-5 text-gray-700">
                              <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {card.popup.professionals.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-semibold text-gray-800">
                          Professionals who support this:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {card.popup.professionals.map((item) => (
                            <span
                              key={item}
                              className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
